import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send } from "lucide-react";
import { adminApi } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";

const fmtDate = (v) => (v ? new Date(v).toLocaleString("ru-RU") : "—");

export const TelegramTab = () => {
  const [users, setUsers] = useState([]);
  const [newId, setNewId] = useState("");
  const [newNote, setNewNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/tg-users");
      setUsers(res.data.items);
    } catch (err) {
      toast.error("Не удалось загрузить список");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const id = parseInt(newId.trim(), 10);
    if (!id) {
      toast.error("Telegram ID — это число (узнать: команда /id в боте)");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/admin/tg-users", { telegram_id: id, note: newNote.trim() || undefined });
      toast.success("Доступ к боту выдан");
      setNewId("");
      setNewNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось добавить");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await adminApi.delete(`/admin/tg-users/${id}`);
      toast.success("Доступ отозван");
      load();
    } catch (err) {
      toast.error("Не удалось удалить");
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="telegram-tab">
      <div className="rounded-xl border border-border bg-white p-4 flex items-start gap-3">
        <Send size={20} className="text-[hsl(var(--primary))] shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">Telegram-бот со статистикой платежей.</span>{" "}
          Каждый час бот присылает сводку допущенным пользователям. Команды: /stats — статистика сейчас,
          /report — отчёт за период с PDF. Чтобы узнать свой Telegram ID — напишите боту команду <code className="bg-muted px-1 rounded">/id</code>,
          затем добавьте ID сюда.
        </div>
      </div>

      <form onSubmit={add} className="flex flex-wrap items-center gap-2" data-testid="add-tg-user-form">
        <Input
          data-testid="new-tg-id-input"
          placeholder="Telegram ID (число)"
          value={newId}
          onChange={(e) => setNewId(e.target.value)}
          className="w-48"
        />
        <Input
          data-testid="new-tg-note-input"
          placeholder="Комментарий (кто это)"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          className="w-64"
        />
        <Button
          data-testid="add-tg-user-button"
          type="submit"
          disabled={busy || !newId.trim()}
          className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white gap-1.5"
        >
          <Plus size={16} /> Дать доступ
        </Button>
      </form>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="tg-users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Telegram ID</TableHead>
                <TableHead>Комментарий</TableHead>
                <TableHead>Добавлен</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    Пока никому не выдан доступ к боту
                  </TableCell>
                </TableRow>
              )}
              {users.map((u) => (
                <TableRow key={u.id} data-testid="tg-user-row">
                  <TableCell className="tabular-nums font-semibold">{u.telegram_id}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.note || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid="delete-tg-user-button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-[hsl(var(--destructive))]"
                      onClick={() => del(u.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
